'use client';

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import {
  deriveActiveFilterDescriptors,
  type DiscoverActiveFilterMultiKey,
  type DiscoverActiveFilterSingleKey,
} from '@/lib/discover/activeFilterCount';
import {
  formatCategoryLabel,
  formatDateLabel,
  formatNeighborhoodLabel,
  formatPriceLabel,
  formatSearchLabel,
} from '@/lib/discover/activeFilterLabels';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import type { DiscoverParams } from '@/lib/discover/url-state';

import {
  DiscoverActiveFiltersView,
  type DiscoverActiveFilter,
  type DiscoverActiveFilterRemoveKey,
} from './DiscoverActiveFiltersView';

/**
 * Convert a single facet value into the chip's display label.
 *
 * WEB-275 (2026-05-14): Replaced the prior `${facetTitle}: ${rawSlug}`
 * interpolation with per-facet humanizers from
 * `@/lib/discover/activeFilterLabels`. Date / neighborhood presets render
 * with full human copy (`Next 7 days`, `NoDa`); categories render bare
 * (`Music`, no `Category:` prefix) per the AC.
 *
 * Fallback chip label for a venue id we cannot name (WEB-741).
 *
 * `venue` values are opaque location ids, and the only client-side source of
 * venue NAMES is `authenticatedEnvelope.followedVenues` — which is
 * authenticated-only and capped (see `buildAuthenticatedEnvelope.ts`). So an id
 * can legitimately be unnameable here: an anonymous viewer on a shared link, or
 * a followed venue past the envelope cap. Rendering the raw id would be worse
 * than a generic word — it is meaningless to a reader and looks like a bug.
 * "Venue" is honest about what the chip removes without pretending to know
 * which one.
 */
const UNNAMED_VENUE_LABEL = 'Venue';

function formatMultiLabel(
  key: DiscoverActiveFilterMultiKey,
  value: string,
  venueNames: ReadonlyMap<string, string>,
): string {
  switch (key) {
    case 'category':
      return formatCategoryLabel(value);
    case 'neighborhood':
      return formatNeighborhoodLabel(value);
    // WEB-741: name it from the envelope when we can, so the chip reads the
    // venue's name rather than an opaque `loc_…` id.
    case 'venue':
      return venueNames.get(value) ?? UNNAMED_VENUE_LABEL;
  }
}

function formatSingleLabel(key: DiscoverActiveFilterSingleKey, value: string): string {
  switch (key) {
    case 'date':
      return formatDateLabel(value);
    case 'price':
      return formatPriceLabel(value);
    case 'q':
      return formatSearchLabel(value);
    // WEB-662 / DPL-12: a boolean facet, so there is no value to interpolate —
    // the chip states the filter itself. Without this case the chip would
    // render the raw stringified `true`.
    case 'followedOnly':
      return 'Only who I follow';
  }
}

function deriveActiveFilters(
  params: DiscoverParams,
  venueNames: ReadonlyMap<string, string>,
): DiscoverActiveFilter[] {
  // Reuse the shared descriptor source so chip ordering, count, and the
  // `Filters · N` trigger label in `MobileFilterDrawer` can never diverge.
  // See `lib/discover/activeFilterCount.ts`.
  return deriveActiveFilterDescriptors(params).map((descriptor) => {
    if (descriptor.removeKey.kind === 'multi') {
      return {
        key: descriptor.key,
        label: formatMultiLabel(descriptor.removeKey.key, descriptor.removeKey.value, venueNames),
        removeKey: descriptor.removeKey,
      };
    }
    const value = params[descriptor.removeKey.key];
    return {
      key: descriptor.key,
      label: formatSingleLabel(descriptor.removeKey.key, value as string),
      removeKey: descriptor.removeKey,
    };
  });
}

export interface DiscoverActiveFiltersProps {
  /**
   * Active city — drives the geo anchor for the shared `/events/discover`
   * subscription the taste signal is read from. Required so the chip can read
   * `meta.personalized` / `authenticatedEnvelope` WITHOUT a second fetch:
   * `buildDiscoverQueryArgs(params, cityCode)` produces the SAME cache key the
   * list view + results bar already subscribe to (cold-load single-fetch
   * invariant — see `lib/discover/queryArgs.ts`).
   */
  readonly cityCode: CityCode;
  readonly className?: string;
}

/**
 * Connected wrapper around `DiscoverActiveFiltersView`. Reads the canonical
 * `/discover` URL state via `useDiscoverParams`, derives the chip array, and
 * writes back removals through `setParams`.
 *
 * Multi-select removals leave an empty array, which `serializeDiscoverParams`
 * omits from the URL — so removing the last value clears the param entirely
 * (`?category=` is never emitted).
 *
 * WEB-1221: the connected strip does not surface the `Matched to taste` chip.
 * The default sort is date order, so a chip that claims taste ranking would
 * stay on after a removal that writes the same sort. The view still accepts
 * `tasteActive` for stories.
 */
export function DiscoverActiveFilters({
  cityCode,
  className,
}: DiscoverActiveFiltersProps): React.ReactElement | null {
  const { params, setParams } = useDiscoverParams();

  // Reuse the shared cold-load subscription (same cache key as the list view +
  // results bar) — never issues its own request.
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { data, isError } = useDiscoverEventsQuery(queryArgs);

  // FRE-1311 — "in flight" vs "confirmed empty". A new `queryArgs` cache key
  // leaves `data` undefined until that key resolves; reading labels off the
  // live payload in that window empties the map and every chip falls through
  // to `UNNAMED_VENUE_LABEL`. The whole response is the pending signal.
  const labelsPending = !isError && data === undefined;

  // WEB-741: id → name for the venue chips, read off the envelope this
  // component already subscribes to (no extra request). Authenticated-only and
  // capped, so unmatched ids fall back to a generic label rather than showing a
  // raw id — see `UNNAMED_VENUE_LABEL`.
  const venueNamesFromResponse = React.useMemo(() => {
    const followedVenues = data?.authenticatedEnvelope?.followedVenues;
    if (!Array.isArray(followedVenues)) return new Map<string, string>();
    return new Map(followedVenues.map((venue) => [venue.id, venue.name]));
  }, [data?.authenticatedEnvelope?.followedVenues]);

  // Stale-while-revalidate: hold the last SETTLED map so established chips
  // keep their names across a refetch. The write is gated
  // on `!labelsPending`, not on map size — gating on non-empty never clears
  // the refs, which leaks a stale label one window later (settled empty, then
  // a later pending render resurrects names two responses old). Clearing on
  // every settled response, empty included, keeps retention to the in-flight
  // window.
  const lastVenueNames = React.useRef<ReadonlyMap<string, string>>(new Map());
  React.useEffect(() => {
    if (!labelsPending) {
      lastVenueNames.current = venueNamesFromResponse;
    }
  }, [venueNamesFromResponse, labelsPending]);

  const venueNames =
    venueNamesFromResponse.size > 0
      ? venueNamesFromResponse
      : labelsPending
        ? lastVenueNames.current
        : venueNamesFromResponse;

  const activeFilters = React.useMemo(
    () => deriveActiveFilters(params, venueNames),
    [params, venueNames],
  );

  const handleRemove = React.useCallback(
    (removeKey: DiscoverActiveFilterRemoveKey) => {
      if (removeKey.kind === 'multi') {
        const next = params[removeKey.key].filter((value) => value !== removeKey.value);
        setParams({ [removeKey.key]: next } as Partial<DiscoverParams>);
        return;
      }
      setParams({ [removeKey.key]: undefined } as Partial<DiscoverParams>);
    },
    [params, setParams],
  );

  return (
    <DiscoverActiveFiltersView
      activeFilters={activeFilters}
      onRemove={handleRemove}
      tasteActive={false}
      className={className}
    />
  );
}
